import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  ProjectFdcDisciplinesQuery,
  useCreateFdcDisciplineMutation,
  useProjectFdcDisciplinesQuery,
  useEditFdcDisciplineMutation,
  useSoftRemoveFdcDisciplineMutation,
  useRestoreFdcDisciplineMutation,
} from 'schema'
import { errorHandler } from 'hooks/errorHandler'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useOptionalDescriptionColumn } from 'components/CIMSTable/common/hooks/useDescriptionColumn'
import { useNameColumn } from 'components/CIMSTable/common/hooks/useNameColumn'
import CIMSTable from 'components/CIMSTable'
import { CIMSPage, Permission } from 'shared'
import {
  simpleCreateNotification,
  simpleEditNotification,
  simpleRemoveNotification,
  simpleRestoreNotification,
} from 'hooks/notificationHandler'
import NameFormItem from 'components/CIMSFormItem/common/NameFormItem'
import DescriptionFormItem from 'components/CIMSFormItem/common/DescriptionFormItem'
import { HelpMessage } from 'utils/constants'
import { ExpandOptions } from 'components/CIMSTable/CIMSTableContext/types/expand'
const Help = HelpMessage.FieldDesignChanges.Disciplines

const Disciplines: React.FC = () => {
  const {
    state: { projectGlobalId: globalId },
  } = useGlobalState()
  const {
    data,
    loading: fetching,
    refetch,
  } = useProjectFdcDisciplinesQuery({
    ...DefaultQueryHookOptions,
    skip: !globalId,
    variables: {
      globalId: globalId!,
      input: { projectId: { equals: globalId } },
    },
  })
  const [create] = useCreateFdcDisciplineMutation({
    onError: errorHandler,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: ({
      createFDCDiscipline: {
        discipline: { name },
      },
    }) => simpleCreateNotification(name),
  })
  const [edit] = useEditFdcDisciplineMutation({
    onError: errorHandler,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: ({
      editFDCDiscipline: {
        discipline: { name },
      },
    }) => simpleEditNotification(name),
  })
  const [remove, { loading: removing }] = useSoftRemoveFdcDisciplineMutation({
    onError: errorHandler,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: ({
      softRemoveFDCDiscipline: {
        discipline: { name },
      },
    }) => simpleRemoveNotification(name),
  })
  const [restore, { loading: restoring }] = useRestoreFdcDisciplineMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      restoreFDCDiscipline: {
        discipline: { name },
      },
    }) => simpleRestoreNotification(name),
  })
  const columns: CIMSColumn<
    ProjectFdcDisciplinesQuery['project']['fdcDisciplines'][0]
  >[] = [
    useNameColumn({
      help: Help.Name,
      sort: { default: 'ascend' },
    }),
    useOptionalDescriptionColumn('Discipline'),
  ]

  const expand: ExpandOptions<
    ProjectFdcDisciplinesQuery['project']['fdcDisciplines'][0]
  > = {
    expandable: ({ fieldDesignChanges }) => fieldDesignChanges.length > 0,
    render: ({ id, fieldDesignChanges }) => ({
      additionalInfo: [
        {
          label: 'Associated FDCs',
          help: Help.AssociatedFDCs,
          type: 'deepLinks',
          showMore: { key: id },
          links: fieldDesignChanges.map(
            ({ id: fdcId, formattedNumber, deleted }) => ({
              label: formattedNumber,
              page: CIMSPage.FDCTracker,
              deep: {
                recordId: fdcId,
                deleted,
              },
            })
          ),
        },
      ],
    }),
  }

  return (
    <CIMSTable
      rowKey="id"
      loading={fetching || removing || restoring}
      records={data?.project.fdcDisciplines}
      columns={columns}
      expand={expand}
      actions={{
        header: {
          onReload: refetch,
          create: {
            entity: 'Discipline',
            permission: Permission.CreateFDCDiscipline,
            modal: {
              body: (
                <>
                  <NameFormItem help={Help.Name} />
                  <DescriptionFormItem entityName="Discipline" />
                </>
              ),
              onOk: async (values: { name: string; description?: string }) => {
                if (!globalId) return errorHandler('projectId is not defined')
                await create({
                  variables: {
                    createFdcDisciplineInput: {
                      projectId: globalId,
                      ...values,
                    },
                  },
                })
                refetch()
              },
            },
          },
        },
        row: {
          edit: {
            permission: Permission.EditFDCDiscipline,
            modal: {
              initialValues: ({ name, description }) => ({ name, description }),
              recordName: 'name',
              body: (
                <>
                  <NameFormItem help={Help.Name} />
                  <DescriptionFormItem entityName="Discipline" />
                </>
              ),
              onOk: async ({ id }, { name, description }) => {
                await edit({
                  variables: {
                    editFdcDisciplineInput: { id, description, name },
                  },
                })
                refetch()
              },
            },
          },
          remove: {
            permission: Permission.RemoveRestoreFDCDiscipline,
            disabled: ({ fieldDesignChanges }) =>
              fieldDesignChanges.length > 0
                ? {
                    message:
                      'Disciplines cannot be deleted if they are associated with one or more Field Design Changes',
                  }
                : false,
            onRemove: async ({ id }) => {
              await remove({ variables: { id } })
              refetch()
            },
          },
          restore: {
            permission: Permission.RemoveRestoreFDCDiscipline,
            onRestore: async ({ id }) => {
              await restore({ variables: { id } })
              refetch()
            },
            metadata: ({ deleted, deletedDate, deletedUser }) => ({
              removed: deleted,
              removedBy: deletedUser,
              removedDate: deletedDate,
            }),
            default: 'active',
          },
        },
      }}
    />
  )
}

export default Disciplines
